import { isMessagePost } from '@web/conversations/utils/guards';
import { exhaustiveSwitchGuard } from '@web/common/utils/exhaustiveSwitchGuard';

import { ActionType, Action } from './conversationProvider.actions';
import {
  ConversationState,
  AddMessageActionPayload,
  AddMessageActionFailPayload,
  AddMessageActionSuccessPayload,
  AddReactionPayload,
  InsertMessageActionPayload,
  RemoveMessageActionPayload,
  RemoveReactionPayload,
  SelectQuotedMessageActionPayload,
  SetConversationActionPayload,
  SetEditMessageActionPayload,
  SetMarkedMessageActionPayload,
  SetReactionPayload,
  SetUnreadMessagesCountPayload,
  SetUnreadMessageMarkerActionPayload,
  UpdateConversationActionPayload,
  UpdateMessageActionPayload,
} from './conversationProvider.types';

const addMessageReducer = (
  state: ConversationState,
  { message }: AddMessageActionPayload
): ConversationState => {
  const allIds = [message.id, ...state.messages.allIds];

  return {
    ...state,
    isArchived: false,
    messages: {
      ...state.messages,
      allIds,
      byId: {
        ...state.messages.byId,
        [message.id]: message,
      },
      visibleIds: allIds,
    },
    newestMessageId: message.id,
  };
};

const addMessageSuccessReducer = (
  state: ConversationState,
  { message, tmpMessageId }: AddMessageActionSuccessPayload
): ConversationState => {
  const allIds = [...state.messages.allIds];
  const tmpIndex = allIds.indexOf(tmpMessageId);
  allIds.splice(tmpIndex, 1, message.id);

  return {
    ...state,
    messages: {
      ...state.messages,
      allIds,
      byId: {
        ...state.messages.byId,
        [tmpMessageId]: undefined,
        [message.id]: message,
      },
      visibleIds: allIds,
    },
    newestMessageId: message.id,
  };
};

const addMessageFailReducer = (
  state: ConversationState,
  { messageId }: AddMessageActionFailPayload
): ConversationState => {
  const message = state.messages.byId[messageId];

  if (!isMessagePost(message)) return state;

  return {
    ...state,
    messages: {
      ...state.messages,
      byId: {
        ...state.messages.byId,
        [messageId]: {
          ...message,
          hasFailedToSubmit: true,
        },
      },
    },
  };
};

const addReactionReducer = (
  state: ConversationState,
  { messageId, reaction, userId }: AddReactionPayload
): ConversationState => {
  const message = state.messages.byId[messageId];

  if (!message || !isMessagePost(message)) return state;

  const currentReactions = message.reactions;
  const currentUserReactions = currentReactions[userId] || [];

  if (currentUserReactions.includes(reaction)) {
    return state;
  }

  const updatedUserRactions = [...currentUserReactions, reaction];

  return {
    ...state,
    messages: {
      ...state.messages,
      byId: {
        ...state.messages.byId,
        [messageId]: {
          ...message,
          reactions: {
            ...currentReactions,
            [userId]: updatedUserRactions,
          },
        },
      },
    },
  };
};

const archiveConversationReducer = (state: ConversationState) => {
  return {
    ...state,
    isArchived: true,
  };
};

const setConversationReducer = (
  state: ConversationState,
  { state: newState }: SetConversationActionPayload
): ConversationState => newState;

const insertMessagesReducer = (
  state: ConversationState,
  { afterId, beforeId, messages }: InsertMessageActionPayload
): ConversationState => {
  const extraIds = messages.map((message) => message.id);
  const newAllIds = [...state.messages.allIds];

  let lookupIndex = newAllIds.indexOf(beforeId || afterId) ?? 0;
  if (beforeId) {
    lookupIndex = lookupIndex + 1;
  } else if (afterId) {
    lookupIndex = lookupIndex - 1;
  }
  lookupIndex = Math.max(0, lookupIndex);
  newAllIds.splice(lookupIndex, 0, ...extraIds);

  const newById = messages.reduce(
    (acc, message) => {
      acc[message.id] = message;
      return acc;
    },
    { ...state.messages.byId }
  );

  return {
    ...state,
    messages: {
      ...state.messages,
      allIds: newAllIds,
      byId: newById,
      visibleIds: newAllIds,
    },
  };
};

const lockScrollReducer = (state: ConversationState) => {
  return {
    ...state,
    hasScrollLocked: true,
  };
};

const muteConversationReducer = (state: ConversationState) => {
  return {
    ...state,
    isSilenced: true,
  };
};

const removeMessageReducer = (
  state: ConversationState,
  { messageId }: RemoveMessageActionPayload
): ConversationState => {
  const message = state.messages.byId[messageId];

  if (!message || !isMessagePost(message)) return state;

  return {
    ...state,
    messages: {
      ...state.messages,
      byId: {
        ...state.messages.byId,
        [messageId]: {
          ...message,
          isDeleted: true,
        },
      },
    },
  };
};

const removeReactionReducer = (
  state: ConversationState,
  { messageId, reaction, userId }: RemoveReactionPayload
): ConversationState => {
  const message = state.messages.byId[messageId];

  if (!message || !isMessagePost(message)) return state;

  const currentReactions = message.reactions;
  const currentUserReactions = currentReactions[userId] || [];
  const updatedUserRactions = currentUserReactions.filter((r) => r !== reaction);

  return {
    ...state,
    messages: {
      ...state.messages,
      byId: {
        ...state.messages.byId,
        [messageId]: {
          ...message,
          reactions: {
            ...currentReactions,
            [userId]: updatedUserRactions,
          },
        },
      },
    },
  };
};

const selectQuotedMessageReducer = (
  state: ConversationState,
  { messageId }: SelectQuotedMessageActionPayload
): ConversationState => {
  const quotedMessage = state.messages.byId[messageId];

  if (!isMessagePost(quotedMessage)) return state;

  return {
    ...state,
    quotedMessageId: messageId,
  };
};

const setEditMessageReducer = (
  state: ConversationState,
  { messageId }: SetEditMessageActionPayload
): ConversationState => {
  const editMessage = state.messages.byId[messageId] || null;

  if (editMessage && !isMessagePost(editMessage)) return state;

  return {
    ...state,
    editMessageId: messageId,
  };
};

const setMarkedMessageReducer = (
  state: ConversationState,
  { id, isHighlighted }: SetMarkedMessageActionPayload
): ConversationState => {
  return {
    ...state,
    markedMessage: {
      id,
      isHighlighted,
    },
  };
};

const setUnreadMessageMarkerReducer = (
  state: ConversationState,
  { customMarker = false, message }: SetUnreadMessageMarkerActionPayload
): ConversationState => {
  // @INFO if message is null, then this reducer was called when
  // conversation is marked as read, so counter is set to 0,
  // but marker remains intact
  if (!message) {
    return {
      ...state,
      unreadMessagesCount: 0,
    };
  }

  const updatedHasCustomUnreadMarker = customMarker || state.hasCustomUnreadMarker;
  const updatedFirstUnreadMessageId =
    customMarker || !state.firstUnreadMessageId ? message.id : state.firstUnreadMessageId;

  return {
    ...state,
    firstUnreadMessageId: updatedFirstUnreadMessageId,
    hasCustomUnreadMarker: updatedHasCustomUnreadMarker,
  };
};

const unarchiveConversationReducer = (state: ConversationState): ConversationState => {
  return {
    ...state,
    isArchived: false,
  };
};

const unlockScrollReducer = (state: ConversationState) => {
  return {
    ...state,
    hasScrollLocked: false,
  };
};

const unmuteConversationReducer = (state: ConversationState): ConversationState => {
  return {
    ...state,
    isSilenced: false,
  };
};

const unselectQuotedMessageReducer = (state: ConversationState): ConversationState => {
  return {
    ...state,
    quotedMessageId: null,
  };
};

const updateConversationReducer = (
  state: ConversationState,
  payload: UpdateConversationActionPayload
): ConversationState => {
  return {
    ...state,
    ...payload,
  };
};

const updateMessageReducer = (
  state: ConversationState,
  { message }: UpdateMessageActionPayload
): ConversationState => {
  const oldMessage = state.messages.byId[message.id];

  if (!oldMessage) return state;

  return {
    ...state,
    messages: {
      ...state.messages,
      byId: {
        ...state.messages.byId,
        [message.id]: {
          ...oldMessage,
          ...message,
        },
      },
    },
  };
};

const setUnreadMessagesCountReducer = (
  state: ConversationState,
  { count }: SetUnreadMessagesCountPayload
) => {
  return {
    ...state,
    unreadMessagesCount: count,
  };
};

const setReactionReducer = (
  state: ConversationState,
  { messageId, reactions }: SetReactionPayload
): ConversationState => {
  const message = state.messages.byId[messageId];

  if (!message || !isMessagePost(message)) return state;

  return {
    ...state,
    messages: {
      ...state.messages,
      byId: {
        ...state.messages.byId,
        [messageId]: {
          ...message,
          reactions,
        },
      },
    },
  };
};

export const reducer = (state: ConversationState, action: Action): ConversationState => {
  switch (action.type) {
    case ActionType.ADD_MESSAGE: {
      return addMessageReducer(state, action.payload);
    }

    case ActionType.ADD_MESSAGE_SUCCESS: {
      return addMessageSuccessReducer(state, action.payload);
    }

    case ActionType.ADD_MESSAGE_FAIL: {
      return addMessageFailReducer(state, action.payload);
    }

    case ActionType.ADD_REACTION: {
      return addReactionReducer(state, action.payload);
    }

    case ActionType.ARCHIVE_CONVERSATION: {
      return archiveConversationReducer(state);
    }

    case ActionType.INSERT_MESSAGES: {
      return insertMessagesReducer(state, action.payload);
    }

    case ActionType.LOCK_SCROLL: {
      return lockScrollReducer(state);
    }

    case ActionType.MUTE_CONVERSATION: {
      return muteConversationReducer(state);
    }

    case ActionType.REMOVE_MESSAGE: {
      return removeMessageReducer(state, action.payload);
    }

    case ActionType.REMOVE_REACTION: {
      return removeReactionReducer(state, action.payload);
    }

    case ActionType.SELECT_QUOTED_MESSAGE: {
      return selectQuotedMessageReducer(state, action.payload);
    }

    case ActionType.SET_CONVERSATION: {
      return setConversationReducer(state, action.payload);
    }

    case ActionType.SET_EDIT_MESSAGE: {
      return setEditMessageReducer(state, action.payload);
    }

    case ActionType.SET_MARKED_MESSAGE: {
      return setMarkedMessageReducer(state, action.payload);
    }

    case ActionType.SET_REACTION: {
      return setReactionReducer(state, action.payload);
    }

    case ActionType.SET_UNREAD_MESSAGES_COUNT: {
      return setUnreadMessagesCountReducer(state, action.payload);
    }

    case ActionType.SET_UNREAD_MESSAGE_MARKER: {
      return setUnreadMessageMarkerReducer(state, action.payload);
    }

    case ActionType.UNARCHIVE_CONVERSATION: {
      return unarchiveConversationReducer(state);
    }

    case ActionType.UNLOCK_SCROLL: {
      return unlockScrollReducer(state);
    }

    case ActionType.UNMUTE_CONVERSATION: {
      return unmuteConversationReducer(state);
    }

    case ActionType.UNSELECT_QUOTED_MESSAGE: {
      return unselectQuotedMessageReducer(state);
    }

    case ActionType.UPDATE_CONVERSATION: {
      return updateConversationReducer(state, action.payload);
    }

    case ActionType.UPDATE_MESSAGE: {
      return updateMessageReducer(state, action.payload);
    }

    default: {
      exhaustiveSwitchGuard(action);
    }
  }
};
